import Link from 'next/link'

import LegalPage from '../../components/legal/LegalPage'

import type { JSX } from 'react'

// Public privacy policy for every Ivy Flip surface: ivyflip.com, the web app,
// the iOS / Android app (which opens this page from Settings) and the Chrome
// extension. Keep the URL stable: the app stores and the mobile app link to it.
export default function PrivacyPolicy (): JSX.Element {
  return (
    <LegalPage
      title='Privacy Policy'
      description='How Ivy Flip collects, uses, and shares personal information across the website, web app, mobile app, and Chrome extension, and the choices you have.'
      updated='September 17, 2026'
    >
      <h2>1. Who we are and what this policy covers</h2>
      <p>
        Ivy Flip is a platform for resellers. It helps you find inventory
        such as retail returns and trailer loads, list items across online
        marketplaces, get alerts about deals, and track your sales. This
        policy explains what personal information we collect, how we use
        it, who we share it with, and the choices you have.
      </p>
      <p>
        It applies to everything we operate (together, the "Services"):
      </p>
      <ul>
        <li>the website at ivyflip.com and its subdomains;</li>
        <li>the Ivy Flip web app, at ivyflip.com/app;</li>
        <li>the Ivy Flip mobile app for iOS and Android;</li>
        <li>the Ivy Flip Chrome extension used for crosslisting;</li>
        <li>emails, text messages, and push notifications we send you.</li>
      </ul>
      <p>
        If you are part of a reseller or Operator program, the agreement
        you signed for that program may contain additional terms about how
        program data is handled. Those terms apply alongside this policy.
      </p>

      <h2>2. Information we collect</h2>

      <h3>Information you give us</h3>
      <ul>
        <li>
          <strong>Account details:</strong> your name, email address, phone
          number, password or sign-in method, and profile settings.
        </li>
        <li>
          <strong>Business details:</strong> your business name, location,
          search radius, the marketplaces you sell on, and any business or
          tax information you choose to provide for a reseller program.
        </li>
        <li>
          <strong>Onboarding answers:</strong> what you tell us about your
          experience and goals when you request access or complete
          onboarding.
        </li>
        <li>
          <strong>Listings and inventory:</strong> item titles,
          descriptions, prices, categories, quantities, photos, and videos
          you create or import, plus your sales and expense records.
        </li>
        <li>
          <strong>Payment details:</strong> when you buy a plan or pay for
          inventory, your payment is processed by Stripe (on the web) or by
          Apple or Google (in the mobile app). We receive confirmation of
          the payment, the last four digits and card type where applicable,
          and your billing name and address. We never receive or store your
          full card number.
        </li>
        <li>
          <strong>Communications:</strong> messages you send us, support
          requests, survey answers, and your consent choices for text
          messages and marketing email.
        </li>
      </ul>

      <h3>Connected marketplace accounts</h3>
      <p>
        When you connect a marketplace such as Facebook Marketplace,
        OfferUp, Craigslist, eBay, or KSL, you authorize Ivy Flip to act on
        your behalf on that marketplace through the Chrome extension or the
        app. To do that we collect and store the account identifiers,
        session information, listing data, and buyer inquiries needed to
        post, update, renew, and remove your listings and to show you
        results. We use this information only to provide the features you
        turned on. Each marketplace has its own privacy policy that governs
        what it collects.
      </p>

      <h3>Information collected automatically</h3>
      <ul>
        <li>
          <strong>Device and usage data:</strong> IP address, device type
          and operating system, browser, app version, pages and screens
          viewed, features used, searches run, and the dates and times of
          your activity.
        </li>
        <li>
          <strong>Crash and performance data:</strong> collected through
          Firebase Crashlytics so we can fix problems.
        </li>
        <li>
          <strong>Analytics:</strong> we use Google Analytics, Firebase
          Analytics, and Mixpanel to understand how the Services are used.
        </li>
        <li>
          <strong>Advertising pixels:</strong> our public website uses the
          Meta (Facebook) pixel and the Reddit pixel to measure our ads and
          reach people who visited our site. These are not used inside the
          logged-in app screens.
        </li>
        <li>
          <strong>Cookies and similar technologies:</strong> see section 7.
        </li>
      </ul>

      <h3>Permissions you grant on your device</h3>
      <ul>
        <li>
          <strong>Location:</strong> with your permission, we use your
          device location to search for listings near you and to show you
          returns and trailer loads within your chosen radius. You can turn
          this off in your device settings at any time.
        </li>
        <li>
          <strong>Camera, photos, and microphone:</strong> with your
          permission, to take, choose, or record photos and videos for your
          listings and to save them to your library.
        </li>
        <li>
          <strong>Push notifications:</strong> with your permission, we
          store a notification token so we can alert you about deals,
          orders, pickups, and account activity.
        </li>
      </ul>

      <h3>Information from other sources</h3>
      <ul>
        <li>
          Retail partners and vendors provide inventory data, manifests,
          and delivery details for the returns and trailer programs.
        </li>
        <li>
          Payment processors tell us whether a payment succeeded, failed,
          or was refunded.
        </li>
        <li>
          Marketplaces return public listing and search results that we
          match against your saved searches.
        </li>
        <li>
          Advertising and analytics partners may tell us which of our ads
          or campaigns brought you to us.
        </li>
      </ul>

      <h2>3. How we use information</h2>
      <ul>
        <li>To create and manage your account and provide the Services.</li>
        <li>To run your searches, match deals, and send you alerts.</li>
        <li>To post, update, and manage your listings on the marketplaces you connect.</li>
        <li>To operate the returns and trailer programs, including claims, pickups, payments, and reconciliation.</li>
        <li>To process payments, subscriptions, and refunds.</li>
        <li>To send service messages about your account, orders, pickups, payments, and support.</li>
        <li>To send marketing email and text messages when you have agreed to receive them.</li>
        <li>To understand how the Services are used and improve them.</li>
        <li>To measure the results of our advertising.</li>
        <li>To keep the Services secure, prevent fraud and abuse, and enforce our terms.</li>
        <li>To comply with law and respond to lawful requests.</li>
      </ul>

      <h2>4. How we share information</h2>
      <p>
        We do not sell your personal information for money. We share it in
        the following ways:
      </p>
      <ul>
        <li>
          <strong>Service providers</strong> that help us run the Services
          and are bound to use the information only for us. They currently
          include Google (Firebase hosting, database, authentication, and
          analytics), Stripe (payments), Apple and Google (in-app
          purchases), Mixpanel (product analytics), Klaviyo (email and
          marketing), Twilio (text messages), and Mailgun (transactional
          email).
        </li>
        <li>
          <strong>Marketplaces you connect.</strong> When you use
          crosslisting, your listing content and account actions are sent
          to the marketplaces you chose.
        </li>
        <li>
          <strong>Retail partners and vendors.</strong> When you claim,
          buy, or pick up inventory through a returns or trailer program,
          we share the information needed to complete it, which can include
          your name, business name, phone number, email address, pickup or
          delivery location, and details of what you claimed.
        </li>
        <li>
          <strong>Advertising and analytics partners.</strong> The Meta and
          Reddit pixels on our public website send those companies
          information about your visit, such as pages viewed and a cookie
          or device identifier. Under some state laws this counts as
          "sharing" for targeted advertising. See section 8 for how to opt
          out.
        </li>
        <li>
          <strong>Legal and safety.</strong> When required by law, court
          order, or government request, or when we believe it is necessary
          to protect the rights, property, or safety of Ivy Flip, our users,
          or others.
        </li>
        <li>
          <strong>Business transfers.</strong> If Ivy Growth, Inc. is
          involved in a merger, acquisition, financing, or sale of assets,
          your information may be transferred as part of that transaction.
          We will notify you of any change in who controls your data.
        </li>
        <li>
          <strong>With your direction.</strong> When you ask us to share
          information or use a feature that shares it.
        </li>
      </ul>

      <h2>5. Text messages</h2>
      <p>
        If you check the text-message consent box on one of our forms, you
        agree to receive automated text messages from Ivy Flip about your
        account, including payment activity, order and pickup status
        updates, inventory alerts, and customer support. You will receive
        up to 2 messages per month from +1 (877) 550-7152. Message and data
        rates may apply. Reply HELP for help or STOP to cancel at any time.
        Consent is not required to make a purchase.
      </p>
      <p>
        Phone numbers and text-message consent collected for this program
        are not shared with third parties or affiliates for their own
        marketing purposes.
      </p>

      <h2>6. Marketing email</h2>
      <p>
        You can unsubscribe from marketing email at any time using the link
        at the bottom of any message or by emailing us. We will still send
        you service messages about your account, such as payment receipts,
        pickup instructions, and security notices, while you have an
        account.
      </p>

      <h2>7. Cookies and tracking technologies</h2>
      <p>
        Our website uses cookies and similar technologies to keep you
        signed in, remember your preferences, measure traffic, and measure
        advertising. Google Analytics, the Meta pixel, and the Reddit pixel
        each set their own cookies or identifiers. You can control cookies
        through your browser settings, and you can opt out of interest-based
        advertising through the settings offered by Meta and Reddit and
        through industry tools such as the Digital Advertising Alliance at
        optout.aboutads.info.
      </p>
      <p>
        Our website does not currently respond to "Do Not Track" browser
        signals. We will honor browser-based Global Privacy Control signals
        as an opt-out of sharing for targeted advertising where the law
        requires it.
      </p>

      <h2>8. Your choices</h2>
      <ul>
        <li>
          <strong>Access and correction:</strong> you can view and update
          your profile, business details, and notification settings inside
          the app.
        </li>
        <li>
          <strong>Delete your account:</strong> in the mobile app, go to
          Settings, then Profile, and choose "Delete my Account". You will
          need to cancel any active subscription first. You can also email
          us and we will delete your account for you. Deleting your account
          removes your profile and listings from Ivy Flip. It does not
          remove listings you already posted on outside marketplaces, and it
          does not delete records we must keep for tax, accounting, or legal
          reasons.
        </li>
        <li>
          <strong>Opt out of sharing for targeted advertising:</strong>
          email <a href='mailto:hello@ivyflip.com'>hello@ivyflip.com</a>
          with the subject "Do Not Sell or Share" or use the cookie and
          browser controls described in section 7.
        </li>
        <li>
          <strong>Marketing email and texts:</strong> unsubscribe from any
          email, or reply STOP to any text.
        </li>
        <li>
          <strong>Device permissions:</strong> turn off location, camera,
          photo, microphone, or notification access at any time in your
          device settings. Some features will not work without them.
        </li>
        <li>
          <strong>Disconnect a marketplace:</strong> remove a connected
          marketplace in the app or extension settings at any time.
        </li>
      </ul>

      <h2>9. Additional information for California residents</h2>
      <p>
        This section applies to California residents under the California
        Consumer Privacy Act as amended by the California Privacy Rights Act
        (together, the "CCPA").
      </p>
      <p>
        <strong>Categories we collect.</strong> In the last 12 months we
        have collected the following categories of personal information:
        identifiers (name, email, phone, account and device identifiers, IP
        address); commercial information (plans bought, inventory claimed,
        listings, and sales records); internet or network activity (pages
        and screens viewed, searches, interactions with our ads); precise
        geolocation, when you allow location access; audio and visual
        information (photos and videos you add to listings); professional
        or business information (your reselling business details); and
        inferences drawn from the above to personalize alerts. We collect
        them from the sources described in section 2 and use them for the
        purposes in section 3.
      </p>
      <p>
        <strong>Sensitive personal information.</strong> Precise geolocation
        and the sign-in information for marketplaces you connect are
        sensitive personal information. We use them only to provide the
        features you requested and never to infer characteristics about
        you.
      </p>
      <p>
        <strong>Selling and sharing.</strong> We do not sell personal
        information. We "share" identifiers and internet activity with Meta
        and Reddit for targeted advertising through the pixels on our public
        website. We do not knowingly sell or share the personal information
        of anyone under 16.
      </p>
      <p>
        <strong>Your rights.</strong> You have the right to know what
        personal information we have collected about you and how we use and
        share it, to receive a copy, to correct inaccurate information, to
        delete your information, to opt out of sharing for targeted
        advertising, to limit the use of sensitive personal information,
        and not to be treated differently for exercising these rights. To
        exercise them, email{' '}
        <a href='mailto:hello@ivyflip.com'>hello@ivyflip.com</a> from the
        email address on your account. We will verify your request by
        confirming control of that account. An authorized agent may submit a
        request on your behalf with your written permission. We respond
        within 45 days and will tell you if we need more time.
      </p>
      <p>
        <strong>Retention.</strong> We keep each category of personal
        information for as long as you have an account and for as long
        afterward as needed for the purposes in section 3, then for the
        periods required by tax, accounting, and legal obligations.
      </p>
      <p>
        We do not offer financial incentives in exchange for personal
        information.
      </p>

      <h2>10. Additional information for Canadian residents</h2>
      <p>
        We collect, use, and disclose personal information in accordance
        with the Personal Information Protection and Electronic Documents
        Act (PIPEDA) and applicable provincial privacy laws. By using the
        Services you consent to the collection, use, and disclosure
        described in this policy. You may withdraw consent at any time,
        subject to legal or contractual restrictions and reasonable notice,
        though some features will then be unavailable. Your information is
        stored and processed on servers in the United States, where it is
        subject to United States law and may be accessible to United States
        authorities. You may request access to or correction of your
        personal information, or make a complaint, by emailing{' '}
        <a href='mailto:hello@ivyflip.com'>hello@ivyflip.com</a>. You also
        have the right to complain to the Office of the Privacy
        Commissioner of Canada or your provincial commissioner.
      </p>

      <h2>11. Users outside the United States and Canada</h2>
      <p>
        The Services are operated from the United States and intended for
        users in the United States and Canada. If you use them from
        elsewhere, your information will be transferred to and processed in
        the United States. Where local law gives you rights to access,
        correct, delete, or object to processing of your information, you
        can exercise them by emailing us.
      </p>

      <h2>12. How long we keep information</h2>
      <p>
        We keep personal information while your account is active and for
        as long as needed to provide the Services, resolve disputes,
        enforce our agreements, and meet legal, tax, and accounting
        obligations. Sales, payment, and inventory-claim records are
        typically kept for seven years. Analytics data is kept in
        aggregated or de-identified form after that.
      </p>

      <h2>13. Security</h2>
      <p>
        We use administrative, technical, and physical safeguards designed
        to protect personal information, including encrypted connections,
        access controls, and hosting with Google Firebase. No method of
        transmission or storage is completely secure, so we cannot
        guarantee absolute security. Keep your password private and tell us
        right away if you think your account has been compromised.
      </p>

      <h2>14. Children</h2>
      <p>
        The Services are for adults 18 and older. We do not knowingly
        collect personal information from anyone under 18. If you believe
        someone under 18 has given us personal information, email us and we
        will delete it.
      </p>

      <h2>15. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. We will post the new
        version here and update the date at the top. If a change
        materially affects how we use information you already gave us, we
        will notify you by email or in the app before it takes effect.
      </p>

      <h2>16. Contact us</h2>
      <p>
        Questions, requests, or complaints about privacy can be sent to{' '}
        <a href='mailto:hello@ivyflip.com'>hello@ivyflip.com</a> or by mail
        to Ivy Growth, Inc., 44 S 17th St, Allentown, PA 18104. See also our{' '}
        <Link href='/legal/terms-of-use' prefetch={false}>Terms of Service</Link>{' '}
        and{' '}
        <Link href='/legal/accessibility' prefetch={false}>Accessibility Statement</Link>.
      </p>
    </LegalPage>
  )
}
